import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { name: 'Trademark Registration', path: '/trademark-registration' },
    { name: 'Copyright Registration', path: '/copyright-registration' },
    { name: 'Company Formation', path: '/company-formation' },
    { name: 'Trademark Search', path: '/trademark-search' },
    { name: 'Amazon Brand Registry', path: '/amazon-brand-registry' },
  ];

  return (
    <nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-3' : 'bg-white/95 backdrop-blur-sm py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/assets/img/brand/logo-dark.png" 
              alt="Fine Trademark Logo" 
              className="h-10 sm:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium transition-colors hover:text-[#E5B80B] ${
                location.pathname === '/' ? 'text-[#E5B80B]' : 'text-[#1E1E1E]'
              }`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div className="relative group">
              <button className="flex items-center font-medium text-[#1E1E1E] hover:text-[#E5B80B] transition-colors">
                Services
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2">
                <div className="py-2">
                  {services.map((service, index) => (
                    <Link
                      key={index}
                      to={service.path}
                      className="block px-4 py-3 text-sm text-[#1E1E1E] hover:bg-[#E5B80B]/10 hover:text-[#E5B80B] transition-colors"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/about-us"
              className={`font-medium transition-colors hover:text-[#E5B80B] ${
                location.pathname === '/about-us' ? 'text-[#E5B80B]' : 'text-[#1E1E1E]'
              }`}
            >
              About Us
            </Link>

            <Link
              to="/contact-us"
              className={`font-medium transition-colors hover:text-[#E5B80B] ${
                location.pathname === '/contact-us' ? 'text-[#E5B80B]' : 'text-[#1E1E1E]'
              }`}
            >
              Contact
            </Link>

            <Button 
              onClick={() => {
                // Trigger Chattrik chat widget
                if (window.CD360 && window.CD360.openChat) {
                  window.CD360.openChat();
                } else if (window.$zoho && window.$zoho.salesiq) {
                  window.$zoho.salesiq.floatwindow.visible('show');
                } else {
                  // Fallback to contact page if chat widget not loaded
                  window.location.href = '/contact-us';
                }
              }}
              className="bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold px-6 transition-all duration-300 hover:scale-105"
            >
              Live Chat
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-[#1E1E1E] hover:text-[#E5B80B] transition-colors"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-3 animate-in slide-in-from-top">
            <Link
              to="/"
              className="block py-2 text-[#1E1E1E] hover:text-[#E5B80B] transition-colors font-medium"
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>

            <div className="space-y-2">
              <div className="font-medium text-[#1E1E1E] py-2">Services</div>
              {services.map((service, index) => (
                <Link
                  key={index}
                  to={service.path}
                  className="block pl-4 py-2 text-sm text-[#1E1E1E] hover:text-[#E5B80B] transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {service.name}
                </Link>
              ))}
            </div>

            <Link
              to="/about-us"
              className="block py-2 text-[#1E1E1E] hover:text-[#E5B80B] transition-colors font-medium"
              onClick={() => setIsOpen(false)}
            >
              About Us
            </Link>

            <Link
              to="/contact-us"
              className="block py-2 text-[#1E1E1E] hover:text-[#E5B80B] transition-colors font-medium"
              onClick={() => setIsOpen(false)}
            >
              Contact
            </Link>

            <Button 
              onClick={() => {
                setIsOpen(false);
                // Trigger Chattrik chat widget
                setTimeout(() => {
                  if (window.CD360 && window.CD360.openChat) {
                    window.CD360.openChat();
                  } else if (window.$zoho && window.$zoho.salesiq) {
                    window.$zoho.salesiq.floatwindow.visible('show');
                  } else {
                    // Fallback to contact page if chat widget not loaded
                    window.location.href = '/contact-us';
                  }
                }, 100);
              }}
              className="w-full bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold"
            >
              Live Chat
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
